import { AsyncActionTree } from "vuex-async-mutations";
import { InstallationState, InstallationModuleStatus } from ".";

import Timeout from "await-timeout";
import { ActionTree } from "vuex/types";
import {
  MechanicInstallation,
  InstallationModule,
  InstallationModuleType,
  InstallationModuleData
} from "@/types";

import clean from "./clean";
import { difference } from "@/utilities";

export const actionsAsync: AsyncActionTree<InstallationState, any> = {
  async ["fetch"]({ commitAsync }, id: number) {
    return commitAsync(this.dispatch("installations/fetch", id), id);
  },

  async ["poll"]({ dispatch, commitAsync }, { id, type, context, timeout = 5000 }) {
    const timer = new Timeout();
    await timer.set(timeout);

    try {
      const status: InstallationModuleStatus = await commitAsync(
        this.$axios.$get(`/installations/${id}/module-status/${type}`),
        {
          id,
          type,
          context,
          pollTimeoutId: timer.id
        }
      );

      switch (status.status) {
        case "success":
          dispatch("fetch", id);
          return status;
        case "failure":
        case "error":
          dispatch("fetch", id);
          return Promise.reject(status);
        default:
          return dispatch("poll", { id, type, context });
      }
    } catch (e) {
      return dispatch("poll", { id, type, context });
    }
  },

  async ["end"]({ dispatch }, id: number) {
    await this.$axios.$post(`/installations/${id}/end`);

    return dispatch("fetch", id);
  },

  async ["end:module"](
    { dispatch },
    { id, moduleType }: { id: number; moduleType: InstallationModuleType }
  ) {
    if (moduleType !== "TENANT") {
      await this.$axios.$post(`/installations/${id}/end`, { module: moduleType });
    }

    return dispatch("fetch", id);
  },

  async ["save"](
    { getters, dispatch, commitAsync },
    { id, moduleType }: { id: number; moduleType: InstallationModuleType }
  ) {
    const moduleData: InstallationModule = getters["module"](id, moduleType);

    if (moduleData) {
      await this.$axios.$patch(`/installations/${id}`, {
        type: moduleType,
        data: clean[moduleType](moduleData.data!)
      });

      return await commitAsync(dispatch("fetch", id), {
        id,
        moduleType
      });
    }

    return Promise.resolve();
  }
};

export const actions: ActionTree<InstallationState, any> = {
  ["update"](
    { commit, rootGetters },
    {
      installation,
      data,
      moduleType
    }: { installation: MechanicInstallation; data: InstallationModuleData; moduleType: string }
  ) {
    const serverInstallation: MechanicInstallation = rootGetters["installations/details"](
      installation.id
    );
    const serverModule = serverInstallation?.modules.find(mod => mod.type === moduleType);
    const diff = difference(data, serverModule!.data!);

    commit("set:module", {
      id: installation.id,
      moduleType,
      data: diff
    });
  },

  ["reset"]: {
    root: true,
    handler({ commit }) {
      commit("reset");
    }
  }
};
